import { useSyncExternalStore } from 'react'

/**
 * This device's receipt printer, kept in this browser only: the shop tablet
 * prints, the manager's laptop doesn't. Nothing here goes to the server.
 */

export type PrintMethod = 'dialog' | 'rawbt'

export interface DevicePrinterSettings {
  /** 'rawbt': straight to the receipt printer. 'dialog': the browser print dialog (A4 slip). */
  method: PrintMethod
  /** Print each new placed order by itself while the Board/backoffice is open. */
  autoPrint: boolean
  /** Auto-print only orders placed after this moment (when it was turned on). */
  autoPrintSince: string | null
  /** Only these stores' orders auto-print; empty = every store this user sees. */
  storeIds: string[]
}

export const DEFAULT_DEVICE_PRINTER: DevicePrinterSettings = {
  method: 'dialog',
  autoPrint: false,
  autoPrintSince: null,
  storeIds: [],
}

const SETTINGS_KEY = 'comforthub.receiptPrinter.device'
const PRINTED_KEY = 'comforthub.receiptPrinter.printed'
/** Order ids remembered as printed: enough for a busy day, small enough to keep. */
export const PRINTED_MEMORY = 500

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode or storage full: the setting lasts for this page only.
  }
}

const listeners = new Set<() => void>()
let current: DevicePrinterSettings = normalize(read(SETTINGS_KEY, DEFAULT_DEVICE_PRINTER))

function normalize(value: Partial<DevicePrinterSettings> | null | undefined): DevicePrinterSettings {
  const v = value ?? {}
  return {
    method: v.method === 'rawbt' ? 'rawbt' : 'dialog',
    autoPrint: Boolean(v.autoPrint),
    autoPrintSince: typeof v.autoPrintSince === 'string' ? v.autoPrintSince : null,
    storeIds: Array.isArray(v.storeIds) ? v.storeIds.filter((id): id is string => typeof id === 'string') : [],
  }
}

export function getDevicePrinter(): DevicePrinterSettings {
  return current
}

export function setDevicePrinter(patch: Partial<DevicePrinterSettings>, now: Date = new Date()): DevicePrinterSettings {
  const next = normalize({ ...current, ...patch })
  // Turning auto-print on starts from now: the backlog is not printed.
  if (next.autoPrint && !current.autoPrint) next.autoPrintSince = now.toISOString()
  if (!next.autoPrint) next.autoPrintSince = null
  // Auto-print only makes sense straight to the printer.
  if (next.method !== 'rawbt') {
    next.autoPrint = false
    next.autoPrintSince = null
  }
  current = next
  write(SETTINGS_KEY, current)
  listeners.forEach((l) => l())
  return current
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key !== SETTINGS_KEY) return
    current = normalize(read(SETTINGS_KEY, DEFAULT_DEVICE_PRINTER))
    listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function useDevicePrinter(): DevicePrinterSettings {
  return useSyncExternalStore(subscribe, getDevicePrinter, getDevicePrinter)
}

export function printedOrderIds(): string[] {
  const ids = read<unknown>(PRINTED_KEY, [])
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
}

export function markOrderPrinted(orderId: string): void {
  const ids = printedOrderIds().filter((id) => id !== orderId)
  ids.push(orderId)
  write(PRINTED_KEY, ids.slice(-PRINTED_MEMORY))
}

/** Tests only. */
export function resetDevicePrinterForTests(): void {
  current = normalize(read(SETTINGS_KEY, DEFAULT_DEVICE_PRINTER))
}
